import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core'
import { formatDisplayHoursToMinutes, formatHours } from '@app/shared/utils/utils'

import { Role } from '@app/features/scoping/models/role.model'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { ScopeElementsService } from '../services/scope-elements.service'
import { UpdateRolePayload, ScopeElementsConfig } from '../models'
import { plainToInstance } from 'class-transformer'
import { TableColumnKey } from '@app/shared/components/ui-components/scope-ui-table/table-column-key.enum'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { getCrFieldToDisplay } from '../utils/change-request/get-cr-field-to-display.util'
import { RoleWithChanges } from '../models/entities-with-changes/role-with-changes.interface'
import { showCrDifferences, getEntityNetDelta } from '../utils/change-request'
import { FormatHoursPipe } from '@app/shared/pipe/formatHours.pipe'
import { ConvertCurrencyPipe } from '@app/shared/pipe/convertCurrency.pipe'
import { isNil } from 'lodash'
import { DEFAULT_PLACEHOLDER } from '@app/shared/constants/default-placeholder.const'
import { Money } from '@app/features/scope-overview/model/money.model'

@Component({
  selector: 'scope-roles-row',
  templateUrl: './roles-row.component.html',
  styleUrls: ['./roles-row.component.scss'],
  providers: [FormatHoursPipe],
})
export class RolesRowComponent {
  @Input() level: number
  @Input() role: RoleWithChanges
  @Input() config: ScopeElementsConfig
  @Input() component: ScopeComponent

  @Output() onOverrideRateCardRoleHours = new EventEmitter<UpdateRolePayload>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()

  constructor(
    protected scopeElementsService: ScopeElementsService,
    private formatHoursPipe: FormatHoursPipe,
    private convertCurrencyPipe: ConvertCurrencyPipe
  ) {}

  overrideRateCardRoleHours() {
    return () => this.onOverrideRateCardRoleHours.emit({ component: this.component, role: this.role })
  }

  updateRoleHours(rateCardMinutes: number) {
    const role = plainToInstance(Role, { ...this.role, rateCardMinutes })
    this.onUpdateRole.emit({ component: this.component, role })
  }

  formatDisplayValue = (val) => formatHours(val / 60)
  parseDisplayValue = (val) => formatDisplayHoursToMinutes(val)

  getFieldToDisplay = (fieldName: string) => getCrFieldToDisplay<RoleWithChanges>(fieldName, this.role)
  showDiff = (fieldName: string): boolean => showCrDifferences<RoleWithChanges>(this.config, this.role, fieldName)

  formatHours = (value: number): string => this.formatHoursPipe.transform(value)
  formatPrice = (value: number | Money): string => {
    const amount = value instanceof Money ? value.amount : value
    return isNil(value) ? DEFAULT_PLACEHOLDER : this.convertCurrencyPipe.transform(amount, this.config?.currency)
  }

  showNetDelta = (): boolean => {
    if (!this.config?.isChangeRequest || !this.config?.showNetDelta) return false
    if (this.role.isAdded || this.role.isRemoved) return true
    const hoursDiff = this.role.changes?.['rateCardTotalHours']
    const priceDiff = this.role.changes?.['agencyPrice']
    return (!!hoursDiff && hoursDiff.old !== hoursDiff.new) || (!!priceDiff && priceDiff.old !== priceDiff.new)
  }

  getHoursDelta(): number {
    return getEntityNetDelta(
      this.role.changes,
      'rateCardTotalHours',
      this.role.getRateCardHours(),
      !!this.role.isAdded,
      !!this.role.isRemoved
    )
  }

  showPriceDelta = (): boolean => this.showNetDelta()

  getPriceDelta(): number {
    return getEntityNetDelta(
      this.role.changes,
      'agencyPrice',
      this.role.getUserSellingPrice({ markup: this.component?.costPlusValue })?.amount,
      !!this.role.isAdded,
      !!this.role.isRemoved
    )
  }

  @HostBinding('class.change-request-removed') get isChangeRequestRemoved() {
    return this.config?.isChangeRequest && (this.config?.showDiff || this.config?.showNetDelta) && this.role.isRemoved
  }
  @HostBinding('class.change-request-added') get isChangeRequestAdded() {
    return (
      this.config?.isChangeRequest &&
      (this.config?.showDiff || this.config?.showNetDelta) &&
      (this.role.isAdded || !this.role.id)
    )
  }

  protected readonly TableColumnKey = TableColumnKey
  protected readonly Privilege = Privilege
  protected readonly DEFAULT_PLACEHOLDER = DEFAULT_PLACEHOLDER
}
